import React, { useEffect, useMemo, useState } from 'react';

import { Box, Flex, Text } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';

import { PaymentMethod } from './PaymentMethod';
import { CautionThinIcon } from '../../../../Icons';
import { Loader } from '../../../../Loader';
import { EColor, ETextVariant } from '../../../../Theme';
import { useSetDefaultPaymentMethod } from '../../../components/AddPaymentMethod/api';
// import { ECardBrand, EPaymentType } from '../types';
import { IPaymentMethodNew } from '../types/paymentMethods';
import { getIsExpiredCard } from '../utils';

// const mockPaymenMethods = [
//   {
//     id: '0',
//     type: EPaymentType.CARD,
//     default: true,
//     card: {
//       brand: ECardBrand.VISA,
//       exp_month: 6,
//       exp_year: 2026,
//       last4: '4242',
//     },
//   },
//   {
//     id: '1',
//     type: EPaymentType.CARD,
//     default: false,
//     card: {
//       brand: ECardBrand.AMEX,
//       exp_month: 6,
//       exp_year: 2026,
//       last4: '5675',
//     },
//   },
//   {
//     id: '2',
//     type: EPaymentType.BANK_ACCOUNT,
//     default: true,
//     us_bank_account: {
//       bank_name: 'Chase Bank',
//       last4: '9999',
//     },
//   },
//   {
//     id: '3',
//     type: EPaymentType.CARD,
//     default: false,
//     card: {
//       brand: ECardBrand.AMEX,
//       exp_month: 6,
//       exp_year: 2023,
//       last4: '4392',
//     },
//   },
//   {
//     id: '4',
//     type: EPaymentType.CARD,
//     default: false,
//     card: {
//       brand: ECardBrand.AMEX,
//       exp_month: 6,
//       exp_year: 2026,
//       last4: '4392',
//     },
//   },
// ];

export const PaymentMethodsList = ({
  paymentMethodsData,
  isLoading,
}: {
  paymentMethodsData: IPaymentMethodNew[];
  isLoading: boolean;
}) => {
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState<
    string | undefined
  >();
  const queryClient = useQueryClient();

  const {
    mutateAsync: setDefaultPaymentMethod,
    isPending: setDefaultPaymentMethodIsPending,
  } = useSetDefaultPaymentMethod();

  const handleSetDefault = async (paymentMethod: IPaymentMethodNew) => {
    try {
      if (!paymentMethod?.id) return;
      await setDefaultPaymentMethod(paymentMethod?.id);
      setDefaultPaymentMethodId(paymentMethod?.id);
      queryClient.invalidateQueries({ queryKey: ['get-payment-methods'] });
    } catch {
      throw new Error("Couldn't set default payment method");
    }
  };

  const isValidPaymentExist = useMemo(() => {
    return paymentMethodsData?.some((paymentMethod) => {
      const card = paymentMethod?.card;
      if (card) {
        const { expirationMonth, expirationYear } = card;
        return !getIsExpiredCard(expirationMonth, expirationYear);
      }
      return true;
    });
  }, [paymentMethodsData]);

  const isDefaultPaymentMethodExpired = useMemo(() => {
    const defaultPaymentMethod = paymentMethodsData?.find(
      (item) => item?.isDefault
    );
    if (defaultPaymentMethod && defaultPaymentMethod.card) {
      return getIsExpiredCard(
        defaultPaymentMethod.card.expirationMonth,
        defaultPaymentMethod.card.expirationYear
      );
    }
  }, [paymentMethodsData]);

  useEffect(() => {
    const defaultMethod = paymentMethodsData?.find((item) => item?.isDefault);
    if (!isLoading) setDefaultPaymentMethodId(defaultMethod?.id);
  }, [paymentMethodsData, isLoading]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Box>
      <Text
        color={EColor.Black}
        fontSize="16px"
        fontWeight="700"
        lineHeight="20px"
      >
        Payment Methods
      </Text>
      {!paymentMethodsData?.length && (
        <Flex alignItems="center" gap="10px" py="12px">
          <Text
            color={EColor.Neutral55}
            fontSize="24px"
            fontWeight="700"
            letterSpacing="-0.72px"
            lineHeight="28px"
            mt="24px"
            variant={ETextVariant.Paragraph6}
          >
            You need to{' '}
            <Text as="span" color={EColor.Black}>
              add a payment method
            </Text>{' '}
            before you can bid.
          </Text>
        </Flex>
      )}

      {!isValidPaymentExist &&
        paymentMethodsData &&
        paymentMethodsData.length > 0 && (
          <Flex alignItems="center" gap="10px" pb="12px" pt="12px">
            <CautionThinIcon height="24px" width="24px" />
            <Box>
              <Text variant={ETextVariant.Small}>
                You don’t have a valid payment method.
              </Text>
              <Text variant={ETextVariant.SmallBold}>
                Please add one to be able to bid.
              </Text>
            </Box>
          </Flex>
        )}

      {((!defaultPaymentMethodId && isValidPaymentExist) ||
        isDefaultPaymentMethodExpired) && (
        <Flex alignItems="center" gap="10px" pb="12px" pt="12px">
          <CautionThinIcon height="24px" width="24px" />
          <Text variant={ETextVariant.Small}>
            Please select a default payment method in order to bid.
          </Text>
        </Flex>
      )}

      <Flex
        flexDir="column"
        gap="0"
        opacity={setDefaultPaymentMethodIsPending ? 0.4 : 1}
      >
        {paymentMethodsData?.map((item) => (
          <PaymentMethod
            isDefault={defaultPaymentMethodId === item?.id}
            item={item}
            key={item?.id}
            setDefaultPaymentMethod={handleSetDefault}
          />
        ))}
      </Flex>
    </Box>
  );
};
